import {
  PublicOutlined,
  LocalOfferOutlined,
  ThumbUpOutlined,
  BuildOutlined,
  CheckCircleOutlined,
  SendOutlined,
  EventAvailableOutlined,
  AssignmentTurnedInOutlined,
  VerifiedOutlined,
  CloseOutlined,
} from "@mui/icons-material";
import { SvgIconComponent } from "@mui/icons-material";
import { ServiceRequestStatusCode } from "../../servicing/models/models";
import { OfferStatusCode } from "../../serviceOffers/models/models";
import { getProviderStatusMessage } from "./statusMessages";

// ════════════════════════════════════════════════════════════════
//  Types
// ════════════════════════════════════════════════════════════════

export type LifecycleStatus = "completed" | "current" | "upcoming" | "failed";

export interface LifecycleStage {
  /** 1-based index */
  step: number;
  /** i18n translation key (resolved by the component) */
  labelKey: string;
  /** MUI icon component for the stage */
  icon: SvgIconComponent;
  /** Stage status relative to current position */
  status: LifecycleStatus;
  /** Optional override label key based on actual SR status (e.g., Draft vs Published for combined stage 1) */
  dynamicLabelKey?: string;
  /** ISO timestamp when this stage was reached */
  timestamp?: string;
  /** i18n key for status description (from statusMessages registry) */
  descriptionKey?: string;
  /** i18n key for action prompt (what the user should do next) */
  actionPromptKey?: string;
  /** i18n key for what the other side sees/is doing */
  otherSideContextKey?: string;
  /** Deadline info for time-sensitive stages */
  deadlineInfo?: { deadline: string; startDate?: string };
  /** Visual urgency level for this stage */
  urgencyLevel?: "info" | "action" | "warning" | "critical";
}

/** Minimal shape of an offer, used to enrich lifecycle stages with timestamps */
export interface EnhancedStageInput {
  statusCode: number;
  createdDate?: string;
  sentDate?: string;
  viewedDate?: string;
  confirmedByProviderDate?: string;
  confirmedArrivalDate?: string;
  confirmationDeadline?: string;
  reviewDate?: string;
  customer?: { name?: string };
}

// ════════════════════════════════════════════════════════════════
//  Owner lifecycle (SR perspective) — 5 stages
// ════════════════════════════════════════════════════════════════

/**
 * Maps an SR statusCode (+ optional accepted offer statusCode) to a 5-stage
 * lifecycle for the Machine Owner view.
 *
 * Stages:
 *   1. Draft/Published (combined — shows "Draft" or "Published" dynamically)
 *   2. Offers Received (Negotiating)
 *   3. Accepted (AwaitingProviderConfirmation / Confirmed)
 *   4. In Progress (InProgress + WorkInProgress)
 *   5. Completed (AwaitingCompletionConfirmation / Completed / Archived)
 */
export function getOwnerLifecycleStages(
  srStatusCode: number,
  acceptedOfferStatusCode?: number
): LifecycleStage[] {
  // Determine which stage (1-5) is "current"
  // IMPORTANT: Status codes are NOT sequential in workflow order.
  // draft=1, openForOffers=2, negotiating=3, inProgress=4, completed=5, archived=6,
  // awaitingProviderConfirmation=7, awaitingCompletionConfirmation=8
  let currentStage = 1;

  switch (srStatusCode) {
    case ServiceRequestStatusCode.draft:
      currentStage = 1; // Draft/Published (shows "Draft")
      break;
    case ServiceRequestStatusCode.openForOffers:
      currentStage = 2; // Published -> waiting for offers (stage 1 completed, stage 2 current)
      break;
    case ServiceRequestStatusCode.negotiating:
      currentStage = 2; // Offers Received
      break;
    case ServiceRequestStatusCode.awaitingProviderConfirmation:
      currentStage = 3; // Accepted (waiting for provider to confirm)
      break;
    case ServiceRequestStatusCode.inProgress:
      // Distinguish between Accepted/Confirmed (stage 3) and actual Work in Progress (stage 4)
      if (acceptedOfferStatusCode !== undefined && acceptedOfferStatusCode >= OfferStatusCode.workInProgress) {
        currentStage = 4; // In Progress (work actually started)
      } else if (acceptedOfferStatusCode !== undefined && acceptedOfferStatusCode === OfferStatusCode.confirmed) {
        currentStage = 3; // Confirmed but not working yet
      } else {
        currentStage = 4; // Default for InProgress
      }
      break;
    case ServiceRequestStatusCode.awaitingCompletionConfirmation:
      currentStage = 5; // Completed (awaiting owner confirmation)
      break;
    case ServiceRequestStatusCode.completed:
      currentStage = 5; // Completed
      break;
    case ServiceRequestStatusCode.archived:
      currentStage = 5; // Show as completed for archived
      break;
  }

  // Determine dynamic label for stage 1 (Draft vs Published)
  const stage1LabelKey = srStatusCode === ServiceRequestStatusCode.draft
    ? "servicing:lifecycle.owner.draft"
    : "servicing:lifecycle.owner.published";

  // Determine dynamic label for stage 5 (Completed vs Archived)
  const stage5LabelKey = srStatusCode === ServiceRequestStatusCode.archived
    ? "servicing:lifecycle.owner.archived"
    : "servicing:lifecycle.owner.completed";

  const stageDefinitions: Array<{ labelKey: string; icon: SvgIconComponent; dynamicLabelKey?: string }> = [
    { labelKey: "servicing:lifecycle.owner.published", icon: PublicOutlined, dynamicLabelKey: stage1LabelKey },
    { labelKey: "servicing:lifecycle.owner.offersReceived", icon: LocalOfferOutlined },
    { labelKey: "servicing:lifecycle.owner.accepted", icon: ThumbUpOutlined },
    { labelKey: "servicing:lifecycle.owner.inProgress", icon: BuildOutlined },
    { labelKey: "servicing:lifecycle.owner.completed", icon: CheckCircleOutlined, dynamicLabelKey: stage5LabelKey },
  ];

  return stageDefinitions.map((def, index) => {
    const step = index + 1;
    let status: LifecycleStatus;

    if (step < currentStage) {
      status = "completed";
    } else if (step === currentStage) {
      status = "current";
    } else {
      status = "upcoming";
    }

    return { step, labelKey: def.dynamicLabelKey || def.labelKey, icon: def.icon, status };
  });
}

// ════════════════════════════════════════════════════════════════
//  Provider lifecycle (Offer perspective) — 6 stages
// ════════════════════════════════════════════════════════════════

/** Terminal (failed) offer statuses */
const TERMINAL_OFFER_STATUSES = new Set([
  OfferStatusCode.rejected,
  OfferStatusCode.expired,
  OfferStatusCode.withdrawn,
]);

/**
 * Maps an Offer statusCode to a 6-stage lifecycle for the Service Provider view.
 *
 * Stages: Offer Sent -> Accepted -> Confirmed -> Working -> Work Done -> Complete
 *
 * For terminal states (rejected/expired/withdrawn), stages up to where the offer
 * reached are shown as completed, with a "failed" marker at the failure point
 * and remaining stages as "upcoming".
 */
export function getProviderLifecycleStages(offerStatusCode: number): LifecycleStage[] {
  const stageDefinitions: Array<{ labelKey: string; icon: SvgIconComponent }> = [
    { labelKey: "serviceOffers:lifecycle.provider.offerSent", icon: SendOutlined },
    { labelKey: "serviceOffers:lifecycle.provider.accepted", icon: ThumbUpOutlined },
    { labelKey: "serviceOffers:lifecycle.provider.confirmed", icon: EventAvailableOutlined },
    { labelKey: "serviceOffers:lifecycle.provider.working", icon: BuildOutlined },
    { labelKey: "serviceOffers:lifecycle.provider.workDone", icon: AssignmentTurnedInOutlined },
    { labelKey: "serviceOffers:lifecycle.provider.complete", icon: VerifiedOutlined },
  ];

  const isTerminal = TERMINAL_OFFER_STATUSES.has(offerStatusCode);

  // Map offer statusCode to the "reached" stage (1-based)
  let reachedStage = 1;

  // Statuses that indicate the offer was at least sent
  if (offerStatusCode >= OfferStatusCode.sent) {
    reachedStage = 1; // Offer Sent (sent, viewed, negotiating)
  }
  if (offerStatusCode >= OfferStatusCode.accepted) {
    // accepted(5) or awaitingConfirmation(9) — but rejected(6), expired(7), withdrawn(8) are terminal
    if (!isTerminal) {
      reachedStage = 2; // Accepted
    }
  }
  if (offerStatusCode >= OfferStatusCode.awaitingConfirmation && !isTerminal) {
    reachedStage = 2; // Still in Accepted phase
  }
  if (offerStatusCode >= OfferStatusCode.confirmed && !isTerminal) {
    reachedStage = 3; // Confirmed
  }
  if (offerStatusCode >= OfferStatusCode.workInProgress && !isTerminal) {
    reachedStage = 4; // Working
  }
  if (offerStatusCode >= OfferStatusCode.workCompleted && !isTerminal) {
    reachedStage = 5; // Work Done
  }
  if (offerStatusCode >= OfferStatusCode.serviceCompleted && !isTerminal) {
    reachedStage = 6; // Complete
  }

  // For terminal statuses, figure out what stage the failure happened at
  let failedStage: number | null = null;
  if (isTerminal) {
    // The offer was at stage 1 (Offer Sent) and got rejected/expired/withdrawn
    // For rejected(6): was at sent/viewed/negotiating -> failed after stage 1
    // For expired(7): validity ended -> failed after stage 1
    // For withdrawn(8): provider withdrew -> failed after stage 1
    failedStage = reachedStage + 1;
    if (failedStage > 6) failedStage = 6;
  }

  return stageDefinitions.map((def, index) => {
    const step = index + 1;
    let status: LifecycleStatus;

    if (isTerminal) {
      if (step <= reachedStage) {
        status = "completed";
      } else if (step === failedStage) {
        status = "failed";
      } else {
        status = "upcoming";
      }
    } else {
      if (step < reachedStage) {
        status = "completed";
      } else if (step === reachedStage) {
        status = "current";
      } else {
        status = "upcoming";
      }
    }

    return { step, ...def, status };
  });
}

// ════════════════════════════════════════════════════════════════
//  Enhanced provider stages — enriches base stages with timestamps
//  and status message context from the offer data
// ════════════════════════════════════════════════════════════════

/**
 * Builds enriched lifecycle stages for the provider view by:
 * 1. Getting base stages from getProviderLifecycleStages
 * 2. Attaching timestamps from the offer input
 * 3. Mapping status-specific description/action keys onto the current stage
 */
export function getEnhancedProviderStages(input: EnhancedStageInput): LifecycleStage[] {
  const baseStages = getProviderLifecycleStages(input.statusCode);

  // Map stage step (1-based) to the timestamp from the offer
  // Stage 1: Offer Sent  → sentDate or createdDate
  // Stage 2: Accepted     → (no direct date, use viewedDate as proxy for "progressed past sent")
  // Stage 3: Confirmed    → confirmedByProviderDate
  // Stage 4: Working      → confirmedArrivalDate (work started around arrival)
  // Stage 5: Work Done    → (no direct timestamp available)
  // Stage 6: Complete     → reviewDate
  const stageTimestamps: Record<number, string | undefined> = {
    1: input.sentDate || input.createdDate,
    2: input.viewedDate,
    3: input.confirmedByProviderDate,
    4: input.confirmedArrivalDate,
    5: undefined,
    6: input.reviewDate,
  };

  // Get status message config for the current status
  const msgConfig = getProviderStatusMessage(input.statusCode);

  return baseStages.map((stage) => {
    const enriched: LifecycleStage = { ...stage };

    // Attach timestamp if the stage has been completed or is current
    if (stage.status === "completed" || stage.status === "current") {
      const ts = stageTimestamps[stage.step];
      if (ts) {
        enriched.timestamp = ts;
      }
    }

    // Only enrich the CURRENT stage with description/action keys
    if (stage.status === "current" && msgConfig) {
      enriched.descriptionKey = msgConfig.descriptionKey;
      if (msgConfig.actionPromptKey) {
        enriched.actionPromptKey = msgConfig.actionPromptKey;
      }
      if (msgConfig.otherSideContextKey) {
        enriched.otherSideContextKey = msgConfig.otherSideContextKey;
      }
      enriched.urgencyLevel = msgConfig.urgencyLevel;

      // Attach deadline info for awaitingConfirmation
      if (input.confirmationDeadline && input.statusCode === OfferStatusCode.awaitingConfirmation) {
        enriched.deadlineInfo = {
          deadline: input.confirmationDeadline,
          startDate: input.confirmedByProviderDate || input.createdDate,
        };
      }
    }

    return enriched;
  });
}
